import axios, { AxiosError } from 'axios'
import isEmpty from 'lodash/isEmpty'
import { buildQueryParams } from '@common/utils/queryParams'
import httpStatuses from '@app/httpStatusCodes'
import { contextOriginService } from '@app/auth/contextOriginService'
import { HEADER_REGGORA_ORIGIN } from '@config/headers'
import authLogger from './auth-logger'

const queryParamSerializer = (params) => {
  return isEmpty(params) ? '' : '?' + buildQueryParams(params)
}

const silentAxiosErrorCodes = [AxiosError.ERR_BAD_REQUEST]
const transientAxiosErrorCodes = [AxiosError.ERR_NETWORK, AxiosError.ERR_CANCELED, AxiosError.ECONNABORTED]

/**
 * Redacts sensitive fields from an object by recursively traversing and replacing
 * any values for keys that contain 'password' (case-insensitive).
 * @param {any} obj - The object to redact
 * @returns {any} A deep copy with sensitive fields redacted
 */
function redactSensitiveData(obj) {
  if (obj === null || obj === undefined) {
    return obj
  }

  // Handle strings that might be JSON
  if (typeof obj === 'string') {
    try {
      const parsed = JSON.parse(obj)
      // If it parsed successfully and contains password fields, redact and re-stringify
      const redacted = redactSensitiveData(parsed)
      return JSON.stringify(redacted)
    } catch (e) {
      // Not JSON, return as-is
      return obj
    }
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => redactSensitiveData(item))
  }

  // Handle non-objects (primitives)
  if (typeof obj !== 'object') {
    return obj
  }

  // Handle objects
  const redacted = {}
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Check if key contains 'password' (case-insensitive)
      if (/password/i.test(key)) {
        redacted[key] = '[REDACTED]'
      } else {
        redacted[key] = redactSensitiveData(obj[key])
      }
    }
  }
  return redacted
}

function requestErrorHandler(error) {
  if (error instanceof AxiosError) {
    // Do not want to handle for request errors that axios knows about (e.g. network errors)
    if (error?.code && silentAxiosErrorCodes.includes(error.code)) {
      return Promise.reject(error)
    }

    // Unknown error structure
    try {
      const redactedError = redactSensitiveData(error.toJSON())
      authLogger.error('API request error: Unexpected error', { 'axios-error': true, 'axios-error-serialized': true, ...redactedError }, error)
    } catch (serializationError) {
      // Fallback if toJSON() fails with native fetch objects
      authLogger.error('API request error: Unexpected error', {
        'axios-error': true,
        'axios-error-serialized': false,
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
      }, error)
    }
    return Promise.reject(error)
  } else {
    // Unknown error
    authLogger.error('API request error: Unexpected error', { 'axios-error': false }, error)
    return Promise.reject(new Error('An unexpected error occurred while processing the request.'))
  }
}

function responseErrorHandler(error) {
  if (error instanceof AxiosError) {
    const status = error.response?.status
    if (error.response?.data?.detail) {
      // Service error responses — preserve status for callers
      const wrapped = new Error(error.response.data.detail)
      wrapped.status = status
      wrapped.data = error.response.data
      return Promise.reject(wrapped)
    }
    if (error.response?.data?.error) {
      // Monolith error responses — preserve status for callers
      const wrapped = new Error(error.response.data.error)
      wrapped.status = status
      wrapped.data = error.response.data
      return Promise.reject(wrapped)
    }
    if (error.code && transientAxiosErrorCodes.includes(error.code)) {
      // Network errors, cancelled requests, timeouts — expected transient failures
      authLogger.warn('API response error: transient', { code: error.code, message: error.message })
      return Promise.reject(error)
    }
    // Unknown error structure
    try {
      const redactedError = redactSensitiveData(error.toJSON())
      authLogger.error('API response error: Unexpected error', { 'axios-error': true, 'axios-error-serialized': true, ...redactedError }, error)
    } catch (serializationError) {
      // Fallback if toJSON() fails with native fetch objects
      authLogger.error('API response error: Unexpected error', {
        'axios-error': true,
        'axios-error-serialized': false,
        message: error?.message,
        code: error?.code,
        status,
      }, error)
    }
    return Promise.reject(error)
  } else {
    // Unknown error
    authLogger.error('API response error: Unexpected error', { 'axios-error': false }, error)
    return Promise.reject(new Error('An unexpected error occurred while processing the response.'))
  }
}

export class AuthApiClient {
  constructor(baseUrl) {
    this.client = axios.create({
      baseURL: baseUrl,
      ...(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test' ? {} : { adapter: 'fetch' }),
    })
    this.client.interceptors.request.use(
      async (config) => {
        const reggora_origin_context = contextOriginService.getToken()
        if (reggora_origin_context) {
          config.headers[HEADER_REGGORA_ORIGIN] = reggora_origin_context
        }
        return config
      },
      (error) => requestErrorHandler(error),
    )
    // By default, axios throws an error if the response status is outside the range of 200-299
    this.client.interceptors.response.use(
      (res) => res.data,
      (error) => responseErrorHandler(error),
    )
  }

  /**
   * Sends a `GET` request.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  async get(url, queryParams = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.get(requestUrl, config)
  }

  /**
   * Sends a `POST` request using an `application/json` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  post(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.post(requestUrl, body, config)
  }

  /**
   * Sends a `POST` request using an `multipart/form-data` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  postForm(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.postForm(requestUrl, body, config)
  }

  /**
   * Sends a `PUT` request using an `application/json` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  put(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.put(requestUrl, body, config)
  }

  /**
   * Sends a `PUT` request using an `multipart/form-data` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  putForm(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.putForm(requestUrl, body, config)
  }

  /**
   * Sends a `PATCH` request using an `application/json` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  patch(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.patch(requestUrl, body, config)
  }

  /**
   * Sends a `PATCH` request using an `multipart/form-data` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  patchForm(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.patchForm(requestUrl, body, config)
  }

  /**
   * Sends an `OPTIONS` request.
   * @param {string} url - The endpoint URL to send the request to
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  options(url, config = {}) {
    return this.client.options(url, config)
  }

  /**
   * Sends a `DELETE` request.
   * @param {string} url - The endpoint URL to send the request to
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  delete(url, config = {}) {
    return this.client.delete(url, config)
  }
}

export const statusCodes = httpStatuses

/**
 * Creates a new AuthApiClient instance with the specified base URL.
 * @param {string} baseUrl
 * @returns {AuthApiClient}
 */
export default function(baseUrl) {
  return new AuthApiClient(baseUrl)
}
